import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Form} from 'react-final-form';
import {useDispatch, useSelector} from 'react-redux';
import {getResumeOptions} from '../actions/ResumeOptionActions';
import {getUser} from '@core/actions/UserActions';
import {RESET_PASSWORD_LABELS} from '@form/actions/PageActions';
import {modalHide, modalShow} from '@core/actions/ModalActions';
import useResumeCalculator from '../hooks/useResumeCalculator';
import useResumeSubmit from '../hooks/useResumeSubmit';
import useFormStep from '../hooks/useFormStep';
import {checkCoupon, COUPON_STATUS_FAILURE, COUPON_STATUS_SUCCESS} from '../actions/CouponActions';
import {
    validateEmail,
    validateFilesRequired,
    validateName,
    validatePhone,
    validatePhoneCode,
    validateStep1
} from '../helpers/ValidateHelper';
import {formatPrice} from '../helpers/PriceHelper';
import AnimatedNumber from '../../common/AnimatedNumber';
import OrderDetailsPage from './OrderDetailsPage';
import OrderInfoPage from './OrderInfoPage';
import SummarySidebar from './SummarySidebar';
import SkeletonLoader from './SkeletonLoader';
import FileUploadModal from './modals/FileUploadModal';
import FinalStepModal from './modals/FinalStepModal';
import {hideOverlay, OverlayContainer, showOverlay} from '../../common/Overlay';
import {notify} from '../../common/Notification';
import axios from 'axios';
import {
    clearCouponFromUrl,
    normalizeResumeFormUrlState,
    readCouponFromUrl,
    readResumeFormStateFromUrl,
    writeCouponToUrl,
    writeResumeFormStateToUrl,
} from '../helpers/ResumeFormUrlStateHelper';

// Read once at module load. Fed by:
//  • URL `?coupon=` query (e.g. via referral link from invite email),
//  • `window.coupon` (BFF injects this from `?coupon=` server-side in
//    `AssetsOrderManager::getSettingsScripts` so the value is available
//    even before client-side JS runs / for SSR-style hydration).
function readInitialCoupon() {
    const fromUrl = readCouponFromUrl();
    if (fromUrl) return fromUrl;

    if (typeof window !== 'undefined' && window.coupon) return String(window.coupon).trim();
    return '';
}

const initialCouponParam = readInitialCoupon();

// Inner component — hooks are safe here
function ResumeFormContent({
                               handleSubmit,
                               values,
                               options,
                               page,
                               setStep,
                               prevPageRef,
                               step1Error,
                               setStep1Error,
                               authErrors,
                               setAuthErrors,
                               isNewCustomerRef,
                               fileDependencies,
                               onOpenFileUpload,
                               payOrder,
                               dispatch,
                               fileUploadRef,
                               formRootRef,
                               servicesRef,
                               customerBalance,
                               useBalance,
                               setUseBalance
                           }) {
    const calculator = useResumeCalculator(values, {useBalance, customerBalance});
    const [slideDirection, setSlideDirection] = useState(null);
    const [animating, setAnimating] = useState(false);
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [resetPasswordEmail, setResetPasswordEmail] = useState('');

    // Track page changes for slide direction
    useEffect(() => {
        if (prevPageRef.current !== null && prevPageRef.current !== page) {
            const direction = page > prevPageRef.current ? 'forward' : 'back';
            setSlideDirection(direction);
            setAnimating(true);
            const timer = setTimeout(() => {
                setAnimating(false);
                setSlideDirection(null);
            }, 400);
            prevPageRef.current = page;
            return () => clearTimeout(timer);
        }
        prevPageRef.current = page;
    }, [page]);

    // Keep order configuration in the URL so refresh/back-forward keeps the draft.
    useEffect(() => {
        writeResumeFormStateToUrl({...values, useBalance});
    }, [values.careerLevel, values.urgency, values.services, values.features, useBalance]);

    // Auto-clear step 1 error when user selects service/feature
    const hasServices = values.services && values.services.length > 0;
    const hasFeatures = values.features && values.features.length > 0;
    useEffect(() => {
        if (step1Error && (hasServices || hasFeatures)) {
            setStep1Error(false);
        }
    }, [step1Error, hasServices, hasFeatures]);

    // Auto-clear file error when files uploaded
    const uploadedFiles = useSelector(state => state.file.data) || [];
    const geoBlocked = useSelector(state => !!state.user?.geoBlocked);
    useEffect(() => {
        if (uploadedFiles.length > 0 && authErrors.files) {
            setAuthErrors(prev => {
                const next = {...prev};
                delete next.files;
                return next;
            });
        }
    }, [uploadedFiles.length]);

    useEffect(() => {
        if (page !== 1 && showResetPassword) {
            setShowResetPassword(false);
        }
    }, [page, showResetPassword]);

    const hasError = step1Error && page === 0;
    const isAccountPage = typeof window !== 'undefined' && window.location.pathname.includes('/account/');
    const isResetPasswordView = page === 1 && showResetPassword;

    const openResetPassword = (email = '') => {
        setResetPasswordEmail(email);
        setAuthErrors({});
        setShowResetPassword(true);
    };

    const closeResetPassword = () => {
        setShowResetPassword(false);
    };

    const slideClass = animating && slideDirection
        ? `resume-step-transition resume-step-transition--${slideDirection}`
        : 'resume-step-transition';

    return <>
        <div className="resume-form__layout">
            <div className="resume-form__main resume-form-block resume-list-visible">
                <form className="resume-form-content form-horizontal"
                      id="resume-wizard-form"
                      onSubmit={handleSubmit}>
                    <div className={slideClass}>
                        {page === 0 && (
                            <OrderDetailsPage
                                options={options}
                                calculator={calculator}
                                hasError={hasError}
                                servicesRef={servicesRef}
                                selectedServices={values.services || []}
                                selectedFeatures={values.features || []}
                                isAccountPage={isAccountPage}
                                fileUploadRef={fileUploadRef}
                                onOpenFileUpload={onOpenFileUpload}
                            />
                        )}
                        {page === 1 && !isAccountPage && (
                            <OrderInfoPage
                                options={options}
                                selectedFeatures={values.features || []}
                                totalPrice={calculator.totalPrice}
                                authErrors={authErrors}
                                isNewCustomerRef={isNewCustomerRef}
                                onClearErrors={(field) => field ? setAuthErrors(prev => {
                                    const next = {...prev};
                                    delete next[field];
                                    return next;
                                }) : setAuthErrors({})}
                                showResetPassword={showResetPassword}
                                resetPasswordEmail={resetPasswordEmail}
                                onForgotPassword={openResetPassword}
                                onResetPasswordBack={closeResetPassword}
                                fileUploadRef={fileUploadRef}
                                formRootRef={formRootRef}
                                setStep={setStep}
                                onOpenFileUpload={onOpenFileUpload}
                            />
                        )}
                    </div>
                </form>
            </div>

            <SummarySidebar
                options={options}
                calculator={calculator}
                values={values}
                page={page}
                hasError={hasError}
                showResetPassword={isResetPasswordView}
                customerBalance={customerBalance}
                useBalance={useBalance}
                onUseBalanceChange={setUseBalance}
                isAccountPage={isAccountPage}
            />
        </div>

        {/* Sticky mobile total price bar */}
        <div className="resume-mobile-sticky-bar">
            <div className="resume-mobile-sticky-bar__price">
                <span className="resume-mobile-sticky-bar__label">Total:</span>
                <span className={`resume-mobile-sticky-bar__amount ${calculator.totalPrice <= 0 ? 'empty' : ''}`}>
                    <AnimatedNumber value={calculator.totalPrice} format={formatPrice}/>
                </span>
            </div>
            {geoBlocked && !isResetPasswordView ? (
                <button
                    type="button"
                    className="resume-mobile-sticky-bar__btn"
                    style={{opacity: 0.5, cursor: 'not-allowed'}}
                    disabled
                >
                    Unavailable
                </button>
            ) : (
                <button
                    className={`resume-mobile-sticky-bar__btn ${hasError ? 'error' : ''}`}
                    type="submit"
                    form={isResetPasswordView ? 'reset-password-active-form' : 'resume-wizard-form'}
                >
                    {isResetPasswordView
                        ? RESET_PASSWORD_LABELS.email
                        : ((isAccountPage || page !== 0) ? 'Place Order' : 'Proceed')}
                </button>
            )}
        </div>

        <FileUploadModal fileDependencies={fileDependencies}/>

        <FinalStepModal
            options={options}
            calculator={calculator}
            values={values}
            onClose={() => dispatch(modalHide())}
            onSkip={() => {
                dispatch(modalHide());
                payOrder(values);
            }}
            onConfirm={(svc, feat) => {
                dispatch(modalHide());
                payOrder(values, svc, feat);
            }}
        />
    </>;
}

export default function Index({onSuccess} = {}) {
    const dispatch = useDispatch();
    const {data: options, loading, error} = useSelector(state => state.resumeOptions);
    const [page, setStep] = useFormStep();
    const {authorized} = useSelector(state => state.user);
    const files = useSelector(state => state.file.data) || [];

    const {signIn, signUp, submitOrder} = useResumeSubmit({setStep, onSuccess});
    const [authErrors, setAuthErrors] = useState({});
    const [step1Error, setStep1Error] = useState(false);
    const isNewCustomerRef = useRef(true);
    const [fileDependencies, setFileDependencies] = useState(null);
    const [fileDependenciesError, setFileDependenciesError] = useState(false);
    const [isFileUploadPending, setIsFileUploadPending] = useState(false);
    const prevPageRef = useRef(null);
    const fileUploadRef = useRef(null);
    const formRootRef = useRef(null);
    const servicesRef = useRef(null);
    const [customerBalance, setCustomerBalance] = useState(0);
    const [useBalance, setUseBalance] = useState(() => readResumeFormStateFromUrl().useBalance === true);

    useEffect(() => {
        let cancelled = false;
        dispatch(getResumeOptions());
        dispatch(getUser());
        if (initialCouponParam) {
            dispatch(checkCoupon(initialCouponParam));
        }
        axios.get('/api/file/dependency')
            .then(res => {
                if (cancelled) return;
                setFileDependencies(res.data);
                setFileDependenciesError(false);
            })
            .catch(() => {
                if (cancelled) return;
                setFileDependenciesError(true);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!isFileUploadPending || !fileDependencies) return;

        hideOverlay();
        dispatch(modalShow('fileUpload'));
        setIsFileUploadPending(false);
    }, [dispatch, fileDependencies, isFileUploadPending]);

    useEffect(() => {
        if (!isFileUploadPending || !fileDependenciesError) return;

        hideOverlay();
        setIsFileUploadPending(false);
        notify('Failed to initialize file upload. Please try again.');
    }, [fileDependenciesError, isFileUploadPending]);

    useEffect(() => () => hideOverlay(), []);

    // Pull store credit only once the customer is signed in — guests
    // can't have a balance and the BFF endpoint requires auth. Re-fetch
    // when authorized flips so the toggle appears immediately after
    // sign-in/sign-up on step 2 of the form.
    useEffect(() => {
        if (!authorized) {
            setCustomerBalance(0);
            setUseBalance(false);
            return;
        }
        let cancelled = false;
        axios.get('/api/balance/view').then(res => {
            if (cancelled) return;
            const v = parseFloat(res.data?.amount_value || res.data?.amount || 0) || 0;
            setCustomerBalance(v);
        }).catch(() => {
            if (!cancelled) setCustomerBalance(0);
        });
        return () => {
            cancelled = true;
        };
    }, [authorized]);

    // Surface invalid-coupon errors as a toast. Success is shown
    // visually in the summary coupon badge, no toast needed.
    const couponState = useSelector(state => state.coupon);
    const couponNotifiedRef = useRef(null);
    useEffect(() => {
        if (!couponState || couponState.status === null) return;
        const fingerprint = `${couponState.code}::${couponState.status}`;
        if (couponNotifiedRef.current === fingerprint) return;
        if (couponState.status === COUPON_STATUS_FAILURE && couponState.code) {
            notify(`Coupon "${couponState.code}" is not valid.`);
            clearCouponFromUrl();
            couponNotifiedRef.current = fingerprint;
        } else if (couponState.status === COUPON_STATUS_SUCCESS) {
            const code = String(couponState.data?.name || couponState.code || '').trim();
            if (code && couponState.data?.isValid) {
                writeCouponToUrl(code);
            }
            couponNotifiedRef.current = fingerprint;
        }
    }, [couponState]);

    const initialValues = useMemo(() => {
        if (!options || !options.careerLevel || !options.urgency) return {};
        const urlState = normalizeResumeFormUrlState(readResumeFormStateFromUrl(), options);

        const defaultCareer = Object.entries(options.careerLevel).find(([, v]) => v.default);
        const defaultUrgency = Object.entries(options.urgency).find(([, v]) => v.default);

        const preCareer = urlState.careerLevel || window.postCareerLevel || (defaultCareer ? defaultCareer[0] : '');
        const preUrgency = urlState.urgency || window.postUrgency || (defaultUrgency ? defaultUrgency[0] : '');

        let preServices = options.serviceDefault || [];
        if (window.postService && !preServices.includes(window.postService)) {
            preServices = [...preServices, window.postService];
        }
        if (urlState.services && urlState.services.length > 0) {
            preServices = urlState.services;
        }

        let phoneCode = String(options.phoneCode || '');
        if (!phoneCode && options.userCountry && options.countries) {
            const c = Object.values(options.countries).find(c => c.code === options.userCountry);
            if (c) phoneCode = String(c.phoneCode);
        }
        if (phoneCode && !phoneCode.startsWith('+')) {
            phoneCode = '+' + phoneCode;
        }

        return {
            careerLevel: preCareer,
            urgency: preUrgency,
            services: preServices,
            features: urlState.features && urlState.features.length > 0
                ? urlState.features
                : (options.featuresDefault || []),
            name: options.userName || '',
            email: options.email || '',
            country: options.userCountry || '',
            phoneCode: phoneCode,
            phoneNumber: options.phoneNumber ? String(options.phoneNumber) : '',
            countryCodeFlag: options.userCountry || '',
            password: '',
            timezone: new Date().getTimezoneOffset() / 60 * -1,
        };
    }, [options]);

    const isAccountPage = typeof window !== 'undefined' && window.location.pathname.includes('/account/');

    const handleSubmit = async (values) => {
        // Guest flow: Step 1 → Step 2 transition. On /account/ we render
        // a single step (PersonalInfo isn't shown for already-authenticated
        // customers), so we fall through to the same submit flow Step 2 runs.
        if (page === 0 && !isAccountPage) {
            const errors = validateStep1(values);
            if (Object.keys(errors).length > 0) {
                setStep1Error(true);
                notify('Please select at least one service to continue.');
                servicesRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
                return errors;
            }
            setStep1Error(false);
            setStep(1);
            formRootRef.current?.scrollIntoView({behavior: 'smooth'});
            return;
        }

        const step1Errors = validateStep1(values);
        if (Object.keys(step1Errors).length > 0) {
            setStep1Error(true);
            notify('Please select at least one service to continue.');
            setStep(0);
            setTimeout(() => servicesRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'}), 100);
            return step1Errors;
        }

        setAuthErrors({});
        const isGuest = !authorized;

        if (isGuest) {
            if (isNewCustomerRef.current) {
                const fieldErrors = {};
                const nameErr = validateName(values.name);
                const emailErr = validateEmail(values.email);
                const countryErr = !values.country ? 'Please select your country' : undefined;
                const phoneErr = validatePhone(values.phoneNumber);
                const phoneCodeErr = values.phoneCode
                    ? validatePhoneCode(values.phoneCode, options.countries)
                    : 'Please enter valid Country code';

                if (nameErr) fieldErrors.name = nameErr;
                if (emailErr) fieldErrors.email = emailErr;
                if (countryErr) fieldErrors.country = countryErr;
                if (phoneErr) fieldErrors.phoneNumber = phoneErr;
                if (phoneCodeErr) fieldErrors.phoneCode = phoneCodeErr;

                if (Object.keys(fieldErrors).length > 0) {
                    setAuthErrors(fieldErrors);
                    formRootRef.current?.scrollIntoView({behavior: 'smooth'});
                    return;
                }

                showOverlay();
                const signUpErrors = await signUp(values);
                if (signUpErrors) {
                    hideOverlay();
                    setAuthErrors(signUpErrors);
                    return;
                }
            } else {
                const fieldErrors = {};
                if (!values.email) fieldErrors.email = 'Please enter your E-mail';
                if (!values.password) fieldErrors.password = 'Please enter your Password';

                if (Object.keys(fieldErrors).length > 0) {
                    setAuthErrors(fieldErrors);
                    formRootRef.current?.scrollIntoView({behavior: 'smooth'});
                    return;
                }

                showOverlay();
                const signInErrors = await signIn(values.email, values.password);
                if (signInErrors) {
                    hideOverlay();
                    setAuthErrors(signInErrors);
                    return;
                }
            }
        }

        if (values.features && values.features.length > 0) {
            const fileErr = validateFilesRequired(files, values.features, options.features || {});
            if (fileErr) {
                hideOverlay();
                notify(fileErr);
                fileUploadRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
                return;
            }
        }

        const needsUpsell = checkUpsell(values, options);
        if (needsUpsell) {
            // Hide the auth-overlay before opening the upsell modal —
            // the modal has its own visual focus, the spinner now would
            // just confuse the customer.
            hideOverlay();
            dispatch(modalShow('finalStep'));
            return;
        }

        // Pass-through to payOrder — submitOrder() inside it will keep
        // its own overlay visible for the order-creation request, so
        // there's no flicker from the customer's perspective.
        payOrder(values);
    };

    const checkUpsell = (values, opts) => {
        const selectedServices = values.services || [];
        const selectedFeatures = values.features || [];
        return (opts.modalServices || []).some(key => !selectedServices.includes(key))
            || (opts.modalFeature || []).some(key => !selectedFeatures.includes(key));
    };

    const payOrder = (values, extraServices = [], extraFeatures = []) => {
        submitOrder({
            ...values,
            services: [...(values.services || []), ...extraServices],
            features: [...(values.features || []), ...extraFeatures],
            useBalance: useBalance && customerBalance > 0,
        });
    };

    const openFileUploadModal = () => {
        if (fileDependencies) {
            dispatch(modalShow('fileUpload'));
            return;
        }

        if (fileDependenciesError) {
            notify('Failed to initialize file upload. Please reload the page and try again.');
            return;
        }

        setIsFileUploadPending(true);
        showOverlay();
    };

    if (loading) return <SkeletonLoader/>;
    if (error || !options) return <div className="critical-error"><h4>Failed to load form data</h4></div>;

    return <div id="resume-form" ref={formRootRef}>
        <Form
            initialValues={initialValues}
            onSubmit={handleSubmit}
        >
            {({handleSubmit, values}) => (
                <ResumeFormContent
                    handleSubmit={handleSubmit}
                    values={values}
                    options={options}
                    page={page}
                    setStep={setStep}
                    prevPageRef={prevPageRef}
                    step1Error={step1Error}
                    setStep1Error={setStep1Error}
                    authErrors={authErrors}
                    setAuthErrors={setAuthErrors}
                    isNewCustomerRef={isNewCustomerRef}
                    fileDependencies={fileDependencies}
                    onOpenFileUpload={openFileUploadModal}
                    payOrder={payOrder}
                    dispatch={dispatch}
                    fileUploadRef={fileUploadRef}
                    formRootRef={formRootRef}
                    servicesRef={servicesRef}
                    customerBalance={customerBalance}
                    useBalance={useBalance}
                    setUseBalance={setUseBalance}
                />
            )}
        </Form>
        <OverlayContainer/>
    </div>;
}
